// src/data/citizenshipData.js
const citizenshipData = {
    "citizenship-naturalization": {
        hero: {
            badge: "Naturalization",
            title: "Citizenship through Naturalization",
            subtitle: "The most common path to U.S. citizenship for permanent residents"
        },
        content: `
      <section class="py-16 lg:py-24 bg-gray-50">
        <div class="max-w-7xl mx-auto px-6">
          <div class="text-center mb-16">
            <h2 class="text-4xl md:text-5xl font-black text-gray-900 mb-6">Naturalization Explained</h2>
            <p class="text-xl text-gray-600 max-w-4xl mx-auto">Naturalization is the process by which a foreign-born person voluntarily becomes a U.S. citizen</p>
          </div>

          <div class="grid lg:grid-cols-3 gap-10 mb-20">
            <div class="bg-white rounded-3xl p-10 shadow-xl border border-gray-100 hover:shadow-2xl transition-all cursor-pointer">
              <div class="text-[3rem] mb-6">5 Years</div>
              <h3 class="text-2xl font-bold text-gray-900 mb-4">General Provision</h3>
              <p class="text-gray-600">Most green card holders qualify after 5 years as permanent residents</p>
            </div>

            <div class="bg-white rounded-3xl p-10 shadow-xl border border-gray-100 hover:shadow-2xl transition-all cursor-pointer">
              <div class="text-[3rem] mb-6">3 Years</div>
              <h3 class="text-2xl font-bold text-gray-900 mb-4">Married to U.S. Citizen</h3>
              <p class="text-gray-600">Spouses of U.S. citizens can apply after just 3 years</p>
            </div>

            <div class="bg-white rounded-3xl p-10 shadow-xl border border-gray-100 hover:shadow-2xl transition-all cursor-pointer">
              <div class="text-[3rem] mb-6">Military</div>
              <h3 class="text-2xl font-bold text-gray-900 mb-4">U.S. Armed Forces</h3>
              <p class="text-gray-600">Special provisions for current and former military members</p>
            </div>
          </div>

          <div class="bg-blue-900 text-white rounded-3xl p-16 text-center">
            <h3 class="text-4xl font-bold mb-6">Ready to Apply?</h3>
            <p class="text-2xl mb-10">File Form N-400 with confidence — approval guaranteed</p>
            <a href="/citizenship/n-400" class="inline-block bg-white text-blue-900 font-bold text-2xl px-16 py-6 rounded-xl hover:bg-gray-100 transition">
              Start N-400 Application Package →
            </a>
          </div>
        </div>
      </section>
    `
    },

    // ==================== Citizenship Through Parents ====================
    "citizenship-parents": {
        hero: {
            badge: "Through Parents",
            title: "Citizenship Through Parents",
            subtitle: "Did you automatically become a U.S. citizen through your parents?"
        },
        content: `
      <section class="py-16 lg:py-24 bg-gray-50">
        <div class="max-w-7xl mx-auto px-6">
          <div class="text-center mb-16">
            <h2 class="text-4xl md:text-5xl font-black text-gray-900 mb-6">You May Already Be a U.S. Citizen</h2>
            <p class="text-xl text-gray-600 max-w-4xl mx-auto">Many people born abroad to U.S. citizen parents are citizens without knowing it</p>
          </div>

          <div class="grid md:grid-cols-2 gap-12 mb-20">
            <div class="bg-white rounded-3xl p-12 shadow-xl border border-gray-100">
              <h3 class="text-3xl font-bold text-gray-900 mb-6">Born Abroad to U.S. Citizen Parent(s)</h3>
              <p class="text-lg text-gray-700 mb-8">If one or both parents were U.S. citizens when you were born, you may have acquired citizenship automatically</p>
              <div class="space-y-4">
                <div class="flex items-start gap-4">
                  <span class="text-4xl">Under 18</span>
                  <p class="text-gray-700">Apply for Consular Report of Birth Abroad (CRBA)</p>
                </div>
                <div class="flex items-start gap-4">
                  <span class="text-4xl">Over 18</span>
                  <p class="text-gray-700">Apply for Certificate of Citizenship (Form N-600)</p>
                </div>
              </div>
            </div>

            <div class="bg-white rounded-3xl p-12 shadow-xl border border-gray-100">
              <h3 class="text-3xl font-bold text-gray-900 mb-6">Derived Citizenship After Birth</h3>
              <p class="text-lg text-gray-700 mb-8">Children under 18 may derive citizenship when a parent naturalizes</p>
              <p class="text-lg text-gray-700 font-semibold">Child Citizenship Act of 2000 made this automatic in most cases</p>
            </div>
          </div>

          <div class="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-3xl p-16 text-center">
            <h3 class="text-4xl font-bold mb-6">Prove Your Citizenship Status</h3>
            <a href="/citizenship/n-600" class="inline-block bg-white text-blue-800 font-bold text-2xl px-16 py-6 rounded-xl hover:bg-gray-100 transition">
              Apply for Certificate of Citizenship (N-600) →
            </a>
          </div>
        </div>
      </section>
    `
    },

    // ==================== N-400 Package ====================
    "n-400": {
        hero: {
            badge: "Form N-400",
            title: "Apply For U.S. Citizenship",
            subtitle: "Naturalization Application — Become a U.S. Citizen"
        },
        price: "$199",
        savings: "Save $950 on average compared to an immigration attorney",
        packageName: "Naturalization Application Package",
        forms: [
            "Form N-400, Application for Naturalization",
            "Form G-1145, e-Notification of Application/Petition Acceptance",
            "Personalized instructions with a supporting document checklist",
            "Live support from citizenhup experts",
            "Guaranteed approval or your money back"
        ]
    },

    // ==================== N-600 Package ====================
    "n-600": {
        hero: {
            badge: "Form N-600",
            title: "Certificate of Citizenship",
            subtitle: "Prove you acquired or derived U.S. citizenship through parents"
        },
        price: "$199",
        savings: "Save $1,200 on average compared to an immigration attorney",
        packageName: "Citizenship Certificate Package",
        forms: [
            "Form N-600, Application for Certificate of Citizenship",
            "Form G-1145, e-Notification of Application/Petition Acceptance",
            "Personalized instructions with a supporting document checklist",
            "Live support from citizenhup experts",
            "Guaranteed approval or your money back"
        ]
    },

    // ==================== N-565 Package ====================
    "n-565": {
        hero: {
            badge: "Form N-565",
            title: "Replace Citizenship Document",
            subtitle: "Lost, stolen, or damaged Certificate of Citizenship/Naturalization?"
        },
        price: "$99",
        savings: "Save $550 on average compared to an immigration attorney",
        packageName: "Citizenship Document Replacement Package",
        forms: [
            "Form N-565, Application to Replace Citizenship Document",
            "Form G-1145, e-Notification of Application/Petition Acceptance",
            "Personalized instructions with a supporting document checklist",
            "Live support from citizenhup experts",
            "Guaranteed approval or your money back"
        ]
    }
};

export default citizenshipData;